// ── BESS peak shaving — the demand (kW) half of a medium-voltage saving ─────────────────
//
// On an MV bill most of the money is NOT kWh. A real bill: 204,823 kWh of energy = RM59,301,
// while capacity (RM30.19/kW) + network (RM66.87/kW) on a 1,236 kW maximum demand = RM119,966
// — 64% of the bill. Solar cannot promise to lower that: one cloudy half hour inside the peak
// window sets the month's MD. A battery can, because it discharges on command.
//
// Ported from the user's BESS Peak Shaving Analyzer (Astern Technologies, 14 Jul 2026) and
// validated against it on real PLO load profiles. Same method:
//   • MD window = weekdays 14:00–22:00 (the MV peak window).
//   • For each MD day, binary-search the LOWEST cap the battery can hold all day, limited by
//     power (kW) and by energy (kWh): every half hour above the cap must be dischargeable.
//   • Month's MD = the highest reading inside the window; shaved = baseline − after.
//   • Saving = shaved kW × credit factor × demand rate. Energy arbitrage is deliberately NOT
//     counted (user 2026-09-25) — it needs night grid charging and loses ~10% round trip.
//
// A LOAD PROFILE IS MANDATORY. Peak shaving depends on the SHAPE of the load, not the total:
// a single sharp spike is cheap to shave, a four-hour plateau is not. Without interval data
// there is no honest BESS proposal — the bill alone can only justify kWh (solar) saving.
// (user 2026-09-25: "if we don't have load profile, we can't peak shaving, so we can't propose")

export const MD_HOUR_START = 14;
export const MD_HOUR_END = 22;
export const POWER_PER_UNIT_KW = 125;      // per battery block — GoodWe 261 kWh and Huawei 241 kWh
export const SLOT_HOURS = 0.5;             // TNB interval data is half-hourly

// ── Engineering derate (Astern "Designing BESS System for Peak Shaving", REV-0, 21 Sep 2026)
// A cabinet's nameplate is not what it can deliver into a peak in year 10. The design document
// SIZES from the energy the battery must DISCHARGE, then grosses it up:
//     installed kWh = discharge kWh ÷ (DoD × discharge efficiency × end-of-life capacity)
// Its worked example: 808 kWh of discharge ÷ (0.90 × 0.95 × 0.7851) ≈ 1,204 kWh installed →
// five 241 kWh cabinets. That chain stays, in installedKwhFor, because sizing is its question.
//
// IT IS NO LONGER APPLIED TO THE SIMULATION ITSELF (derate now defaults FALSE), for two
// reasons found on 2026-10-26 by running the analyzer HTML against the same load profile:
//
//   1. IT DISAGREED WITH THE ANALYZER. Its runSimulation uses `Emax = capacityKwh * u` and
//      says so: "Year-0 energy always uses the user's actual nameplate capacity". With the
//      derate off, this module reproduces that tool to one decimal at EVERY unit count on
//      the All Cosmos profile - 123.0 / 185.1 / 235.8 / 285.6 / 335.0 / 382.9 / 429.8 /
//      474.5 kW - and with it on, read 106 / 150 / 186 / 220. (user 2026-10-26: "please
//      use exactly")
//
//   2. IT DOUBLE-COUNTED THE HAIRCUT, twice over. The 90% credit factor is already defined
//      as "a single conservative buffer covering round-trip efficiency loss, operating
//      margin below the theoretical safe cap, and real-world derating" - so DoD and
//      efficiency were being charged to the customer twice. And END_OF_LIFE_CAPACITY is a
//      YEAR-10 figure, while bessFinancials separately degrades the saving across 20 years
//      on OEM_RATIOS, whose year-10 ratio is 0.7893 - the same number again. Year 20 came
//      out at 0.7851 × 0.687 = 54% of nameplate, ageing the pack twice.
//
// The constants stay exported: they are still correct for sizing, and `derate: true` remains
// available for anyone who wants the end-of-life view explicitly.
export const DOD = 0.90;                   // depth of discharge
export const DISCHARGE_EFFICIENCY = 0.95;
export const END_OF_LIFE_CAPACITY = 0.7851; // 10-year degradation floor
export const USABLE_FRACTION = DOD * DISCHARGE_EFFICIENCY * END_OF_LIFE_CAPACITY;  // ≈ 0.6713

/** Installed kWh needed to deliver `dischargeKwh` at end of life — the document's chain. */
export const installedKwhFor = (dischargeKwh) => (Number(dischargeKwh) || 0) / USABLE_FRACTION;

// Demand charges an MV customer pays per kW of maximum demand: capacity + network.
//
// The 1.6% KWTBB rides on them too and a shaved kW removes it as well, so crediting it is
// defensible — but the house proposal (Astern's BESS analyzer, and the Paragon deck it
// produced) quotes RM 97.06, and a proposal that claims more than the deck beside it is
// the one that gets argued with. The levy is left out on purpose; it is the conservative
// side of a 1.6% question. (user 2026-10-05, after the Paragon reconciliation)
export const MV_CAPACITY_KW = 30.19;
export const MV_NETWORK_KW = 66.87;
export const MV_KWTBB_RATE = 0.016;        // kept for reference; NOT in the saving rate
export const MV_DEMAND_RATE_PER_KW = MV_CAPACITY_KW + MV_NETWORK_KW;  // = 97.06

// OEM warranty curve from the analyzer: usable energy by year, as a ratio of year 0. Year 1 of
// the projection uses index 0. The absolute kWh figures are a 261 kWh/unit reference, so only
// the SHAPE is portable — scale it by whatever nameplate the card carries.
// Years 11-20 corrected 2026-10-26 to the analyzer's own array, verified by reading
// b6b4b484-bess_peak_shaving_analyzer14072026_2.html. Ours had drifted by a digit or two
// a year (1216 against 1215, 1076 against 1074), which moved only the 20-year return.
const OEM_6UNITS = [1566, 1497, 1446, 1404, 1368, 1336, 1308, 1282, 1258, 1236,
    1215, 1196, 1178, 1161, 1145, 1129, 1115, 1101, 1087, 1074];
export const OEM_RATIOS = OEM_6UNITS.map(e => e / OEM_6UNITS[0]);

// ── Time helpers ────────────────────────────────────────────────────────────────────────
// Read the clock from the Excel SERIAL's own fraction, never from a UTC Date's getHours() —
// the +8 offset would shift readings into the wrong day and wreck the peak/off-peak split.
//
// THE STAMP IS THE END OF ITS INTERVAL. TNB's file says so in the header — "Date / End Time"
// — so a reading stamped 14:00 covers 13:30–14:00 and belongs OUTSIDE the 14:00–22:00 window,
// while the 22:00 one covers 21:30–22:00 and belongs inside. Everything below therefore works
// on the interval's START, one slot earlier.
//
// This was the whole gap against the house proposal: reading the stamp as the start instead
// put the window one slot out, and on the Paragon profile that alone cost 6.2 kW of simulated
// shaving and RM 6,540 a year. With it corrected, this module reproduces that deck to the
// decimal at every unit count. (user 2026-10-05, after the reconciliation)
export const SLOT_MINUTES = 30;
const serialParts = (rawSerial) => {
    const serial = rawSerial - SLOT_MINUTES / 1440;   // stamp -> interval start
    const utcDays = Math.floor(serial - 25569);
    const dateInfo = new Date(utcDays * 86400 * 1000);
    const frac = serial - Math.floor(serial) + 1e-7;
    let total = Math.floor(86400 * frac);
    const seconds = total % 60; total -= seconds;
    const hour = Math.floor(total / 3600);
    const minute = Math.floor(total / 60) % 60;
    const d = new Date(dateInfo.getUTCFullYear(), dateInfo.getUTCMonth(), dateInfo.getUTCDate(), hour, minute, seconds);
    const p2 = (n) => String(n).padStart(2, '0');
    const month = `${d.getFullYear()}-${p2(d.getMonth() + 1)}`;
    return { hour: hour + minute / 60, dow: d.getDay(), month, dayKey: `${month}-${p2(d.getDate())}` };
};

/** Annotate raw {serial, kwImport} records (parseTnbRows) with day / month / MD-window flags. */
export function deriveBessRows(records) {
    return (records || []).map(r => {
        const p = serialParts(Number(r.serial) || 0);
        const kw = Math.max(Number(r.kwImport) || 0, 0);
        return { ...r, kw, dayKey: p.dayKey, month: p.month, hour: p.hour,
            isMdWindow: p.dow >= 1 && p.dow <= 5 && p.hour >= MD_HOUR_START && p.hour < MD_HOUR_END };
    });
}

/**
 * The lowest cap a battery of `powerKw` / `energyKwh` can hold across one day's readings:
 * nothing may exceed its power, and the total energy discharged must fit the pack.
 */
export function findSafeCap(loadArr, powerKw, energyKwh) {
    let low = 0, high = Math.max(...loadArr, 0);
    for (let i = 0; i < 40; i++) {
        const mid = (low + high) / 2;
        let allOk = true, discharged = 0;
        for (const v of loadArr) {
            const d = Math.max(v - mid, 0);
            if (d > powerKw) allOk = false;
            discharged += d;
        }
        if (allOk && discharged * SLOT_HOURS <= energyKwh) high = mid; else low = mid;
        if (high - low <= 0.5) break;
    }
    return high;
}

/**
 * Per-month maximum demand before and after `units` batteries, plus the kW shaved.
 * `derate` applies the design document's DoD / efficiency / end-of-life chain to the energy the
 * pack can actually deliver; pass false to simulate on raw nameplate (the analyzer's basis).
 */
export function simulatePeakShaving(rows, { units, capacityKwh, powerPerUnitKw = POWER_PER_UNIT_KW, derate = false }) {
    const powerKw = units * powerPerUnitKw;
    const nameplateKwh = units * capacityKwh;
    const energyKwh = derate ? nameplateKwh * USABLE_FRACTION : nameplateKwh;
    const byDay = {};
    rows.forEach((r, i) => { if (r.isMdWindow) (byDay[r.dayKey] ||= []).push(i); });

    const after = rows.map(r => r.kw);
    Object.values(byDay).forEach(idxs => {
        const load = idxs.map(i => rows[i].kw);
        const cap = findSafeCap(load, powerKw, energyKwh);
        idxs.forEach((i, k) => { after[i] = Math.min(load[k], cap); });
    });

    const months = [...new Set(rows.filter(r => r.isMdWindow).map(r => r.month))].sort();
    const monthly = months.map(m => {
        const idxs = rows.map((r, i) => (r.month === m && r.isMdWindow ? i : -1)).filter(i => i >= 0);
        const baseline = Math.max(...idxs.map(i => rows[i].kw), 0);
        const afterMd = Math.max(...idxs.map(i => after[i]), 0);
        return { month: m, baseline, after: afterMd, shaved: Math.max(baseline - afterMd, 0) };
    });
    const avgShaved = monthly.length ? monthly.reduce((s, m) => s + m.shaved, 0) / monthly.length : 0;

    // The worst MD day — what the design document sizes from: the cap it holds, the energy it
    // must discharge and the peak discharge power, so the proposal can show the working.
    let worstDay = null;
    Object.entries(byDay).forEach(([dayKey, idxs]) => {
        const discharge = idxs.reduce((s, i) => s + Math.max(rows[i].kw - after[i], 0), 0) * SLOT_HOURS;
        const peakKw = Math.max(...idxs.map(i => rows[i].kw), 0);
        const power = Math.max(...idxs.map(i => rows[i].kw - after[i]), 0);
        if (!worstDay || discharge > worstDay.dischargeKwh) {
            worstDay = { dayKey, dischargeKwh: discharge, peakKw, capKw: Math.max(...idxs.map(i => after[i]), 0), dischargePowerKw: power };
        }
    });

    // The worst day's full 24 hours, load and held-down load side by side. The proposal draws
    // it: a table of monthly maxima never shows the customer the SHAPE of what is being paid
    // for, and the shape is the whole argument for a battery. (user 2026-09-26)
    const worstDaySeries = worstDay
        ? rows.map((r, i) => (r.dayKey === worstDay.dayKey ? { hour: r.hour, kw: r.kw, after: after[i] } : null))
            .filter(Boolean).sort((a, b) => a.hour - b.hour)
        : [];

    /* THE HIGHEST-DEMAND DAY — a different day from worstDay above, and the one the CHART
       should draw. worstDay is the heaviest ENERGY day: it drains the pack hardest, which
       is what SIZES the pack, and it is often not especially tall. The day that sets the
       month's MD bill is the tallest one, and that is what a customer recognises.

       Selected on the in-window peak, exactly as the analyzer's computeTop3Days does, so
       our chart lands on its Top-3 #1 tab. On the All Cosmos profile that is 2026-04-24
       at 869 kW, where ours previously drew 2026-03-04 at 770 kW and still captioned it
       "the worst demand day". (user 2026-10-26: "how to match my graph?")

       peakKw is the WHOLE DAY's maximum while capKw is the in-window hold, which is how
       the analyzer's computeDayUnitStats reports them - so MD shaved is measured against
       the number printed beside it. */
    let peakDayKey = null, peakDayWindowKw = -Infinity;
    Object.entries(byDay).forEach(([dayKey, idxs]) => {
        const windowPeak = Math.max(...idxs.map(i => rows[i].kw), 0);
        if (windowPeak > peakDayWindowKw) { peakDayWindowKw = windowPeak; peakDayKey = dayKey; }
    });
    let peakDay = null;
    let peakDaySeries = [];
    if (peakDayKey) {
        const dayIdx = rows.map((r, i) => (r.dayKey === peakDayKey ? i : -1)).filter(i => i >= 0);
        const windowIdx = byDay[peakDayKey];
        const peakKw = Math.max(...dayIdx.map(i => rows[i].kw), 0);
        const capKw = Math.max(...windowIdx.map(i => after[i]), 0);
        peakDay = {
            dayKey: peakDayKey,
            peakKw,
            windowPeakKw: peakDayWindowKw,
            capKw,
            shavedKw: Math.max(0, peakKw - capKw),
            dischargeKwh: windowIdx.reduce((s, i) => s + Math.max(rows[i].kw - after[i], 0), 0) * SLOT_HOURS,
        };
        peakDaySeries = dayIdx
            .map(i => ({ hour: rows[i].hour, kw: rows[i].kw, after: after[i] }))
            .sort((a, b) => a.hour - b.hour);
    }

    return {
        units, powerKw, energyKwh, nameplateKwh, monthly, avgShavedKw: avgShaved, after, worstDay,
        worstDaySeries, peakDay, peakDaySeries,
        // What the document would quote: the installed size needed for that worst day.
        requiredInstalledKwh: worstDay ? installedKwhFor(worstDay.dischargeKwh) : 0,
    };
}

/**
 * Money. `demandRatePerKw` defaults to the MV capacity + network + KWTBB figure; `creditFactor`
 * is the share of the shaved kW the customer is credited for (0.9 = the analyzer's default,
 * a safety margin against a month where the battery cannot hold the cap).
 */
export function bessFinancials(sim, {
    demandRatePerKw = MV_DEMAND_RATE_PER_KW,
    creditFactor = 0.9,
    pricePerKwh,
    capex: capexOverride,
    years = 20,
} = {}) {
    const monthly = sim.monthly.map(m => ({ ...m, saving: m.shaved * creditFactor * demandRatePerKw }));
    // With more than two months of readings the real monthly totals beat an average; with fewer,
    // extrapolate the average across the year (the analyzer's rule).
    const annualYear1 = monthly.length > 2
        ? (monthly.length >= 12
            ? monthly.slice(0, 12).reduce((s, m) => s + m.saving, 0)
            : (monthly.reduce((s, m) => s + m.saving, 0) / monthly.length) * 12)
        : sim.avgShavedKw * creditFactor * demandRatePerKw * 12;

    // Capex is priced on the NAMEPLATE the customer buys, not the derated usable energy.
    const capex = capexOverride != null ? capexOverride : (sim.nameplateKwh ?? sim.energyKwh) * (pricePerKwh || 0);
    const paybackYears = annualYear1 > 0 ? capex / annualYear1 : Infinity;

    /* THE SAME PAYBACK WITHOUT THE CREDIT HAIRCUT — the analyzer's "@100%" column, its
       "technical potential": what the simulation says the battery shaves, before the
       conservative factor that covers round-trip losses and operating margin. Shown
       beside the credited figure so the pair brackets the answer instead of quoting one
       number as if it were exact. It is NOT used for the <4-year test; that stays on the
       credited saving, which is what the customer is promised. (user 2026-11-03) */
    const annualYear1FullCredit = creditFactor > 0 ? annualYear1 / creditFactor : annualYear1;
    const paybackYearsFullCredit = annualYear1FullCredit > 0 ? capex / annualYear1FullCredit : Infinity;

    let cumulative = -capex;
    const cashflow = [cumulative];
    for (let y = 1; y <= years; y++) {
        cumulative += annualYear1 * (OEM_RATIOS[y - 1] ?? OEM_RATIOS[OEM_RATIOS.length - 1]);
        cashflow.push(cumulative);
    }
    return {
        ...sim, monthly, annualYear1, monthlyAvgSaving: annualYear1 / 12, capex, paybackYears,
        annualYear1FullCredit, paybackYearsFullCredit, creditFactor, cashflow,
    };
}

// Corporate tax, capital allowance and GITA — the three payback lines the design document
// quotes (none / CA / CA + GITA). CA absorbs the whole cost over six claim years at 24% tax;
// GITA gives a further 24% of qualifying capex. Both reduce the EFFECTIVE cost, so they shorten
// payback without touching the saving. (user 2026-09-25)
export const CORPORATE_TAX = 0.24;
export const GITA_RATE = 0.24;
export const SST_ON_CAPEX = 0.012;   // 6% on the 20% portion — the same rule as the solar price

/**
 * Run 1..maxUnits and rank them.
 * `best` is chosen on MARGINAL payback, not lowest total payback: the first block always looks
 * best on its own because it shaves the tallest slice, so "lowest payback" would recommend one
 * block on every site. Instead keep adding while the NEXT block still pays for itself inside
 * `qualifyYears` (4 in the analyzer). On a flat peak the second block adds almost nothing and
 * is rejected; on a spiky one it is accepted. (user 2026-09-25)
 */
export function analyseBess(records, {
    minUnits = 6, maxUnits = 40, capacityKwh = 261, powerPerUnitKw = POWER_PER_UNIT_KW,
    demandRatePerKw = MV_DEMAND_RATE_PER_KW, creditFactor = 0.9, pricePerKwh = 1200,
    qualifyYears = 4, derate = false, extraUnitsAfterQualifying = 2,
} = {}) {
    const rows = deriveBessRows(records);
    if (!rows.some(r => r.isMdWindow)) return { rows: rows.length, mdReadings: 0, results: [], best: null };

    /* HOW FAR THE LADDER RUNS. It used to stop at a fixed count, which on a big peak
       cut the list off while every block shown still qualified - the agent could not
       see whether a ninth or tenth unit also paid back, and the recommendation was
       capped by the list length rather than by the economics. Now it keeps simulating
       while units still pay back inside `qualifyYears` and stops two units past the
       last one that did, so the first rejected sizes are visible as the reason to stop.
       `maxUnits` is only a runaway guard; saving saturates once the peak is fully
       shaved, so payback always rises and the loop always terminates on its own.
       (user 2026-11-02: "can go more than 10unit? until <4years roi +2 unit out") */
    const results = [];
    let lastQualifying = 0;
    for (let u = 1; u <= maxUnits; u++) {
        const sim = simulatePeakShaving(rows, { units: u, capacityKwh, powerPerUnitKw, derate });
        const fin = bessFinancials(sim, { demandRatePerKw, creditFactor, pricePerKwh });
        const prev = results[u - 2];
        const addedCapex = fin.capex - (prev?.capex || 0);
        const addedAnnual = fin.annualYear1 - (prev?.annualYear1 || 0);
        const paybackWithCA = fin.annualYear1 > 0 ? (fin.capex * (1 - CORPORATE_TAX)) / fin.annualYear1 : Infinity;
        results.push({
            ...fin,
            /* The tick is the analyzer's: GROSS payback under the threshold, before any
               capital allowance. It judged this on the after-CA payback until 2026-10-26,
               which ticked more unit counts than the analyzer did on the same data and so
               recommended a different system. CA and GITA are still computed and still
               shown on the card - they are a real part of the customer's return - they
               just no longer decide what is proposed.
               (user 2026-10-26: "yes less than 4years suggestion is good") */
            qualifies: fin.paybackYears < qualifyYears,
            addedShavedKw: fin.avgShavedKw - (prev?.avgShavedKw || 0),
            addedMonthlySaving: fin.monthlyAvgSaving - (prev?.monthlyAvgSaving || 0),
            // Marginal payback is judged on the same after-CA basis as the tick.
            marginalPaybackYears: addedAnnual > 0 ? (addedCapex * (1 - CORPORATE_TAX)) / addedAnnual : Infinity,
            marginalPaybackGross: addedAnnual > 0 ? addedCapex / addedAnnual : Infinity,
            // Payback after incentives: CA writes the cost off against tax, GITA adds 24%.
            paybackWithCA,
            paybackWithCAGita: fin.annualYear1 > 0 ? (fin.capex * (1 - CORPORATE_TAX - GITA_RATE)) / fin.annualYear1 : Infinity,
            // The same two incentive lines on the UNCREDITED saving, so the 100% case can be
            // read as a complete set rather than a single number borrowed from the other
            // column. (user 2026-11-03: "have two payback card, one is 100% and another one
            // is 90% one")
            paybackWithCAFull: fin.annualYear1FullCredit > 0
                ? (fin.capex * (1 - CORPORATE_TAX)) / fin.annualYear1FullCredit : Infinity,
            paybackWithCAGitaFull: fin.annualYear1FullCredit > 0
                ? (fin.capex * (1 - CORPORATE_TAX - GITA_RATE)) / fin.annualYear1FullCredit : Infinity,
        });
        if (results[results.length - 1].qualifies) lastQualifying = u;
        if (u >= minUnits && u >= lastQualifying + extraUnitsAfterQualifying) break;
    }
    /* THE LARGEST QUALIFYING UNIT COUNT - `Math.max(...qualifying)`, exactly as the
       analyzer's recommendation banner does it.

       This used to walk up while each MARGINAL block still paid for itself after CA, and
       stop at the first that did not. That rule is defensible on its own terms - the first
       block always flatters the average, because it shaves the tallest slice - but it
       recommended 2 units where the analyzer recommended 3 on the same profile, and a
       proposal that disagrees with the tool it was reconciled against is the one that gets
       argued with. marginalPaybackYears is still computed and still shown per unit chip.
       (user 2026-10-26: "yes less than 4years suggestion is good")

       Math.max rather than "stop at the first failure": the two agree while payback rises
       monotonically with size, and where it does not, this takes the same answer the
       analyzer would. */
    const qualifying = results.filter(r => r.qualifies);
    const best = qualifying.length
        ? qualifying.reduce((a, b) => (b.units > a.units ? b : a))
        : results[0];
    return {
        rows: rows.length, mdReadings: rows.filter(r => r.isMdWindow).length,
        months: results[0].monthly.length, qualifyYears, results,
        best: best || results[0],
        shortestPayback: results.reduce((a, b) => (b.paybackYears < a.paybackYears ? b : a), results[0]),
    };
}
